import _ from 'lodash';
import moment from 'moment';
import DepsService from 'services/dependencies';
import { evalMeta } from 'utils/grid/grid';

const namespace = 'documents';
const DOCUMENTS_CONTEXT = 'documents';
const docDateFormat = 'M/D/YYYY';

export const SET_CATEGORIES = `${namespace}_SET_CATEGORIES`;
export const SET_FILES = `${namespace}_SET_FILES`;
export const SET_TAGS = `${namespace}_SET_TAGS`;
export const SET_IS_LOADING = `${namespace}_SET_IS_LOADING`;

const DEFAULT_MIME_TYPE = 'text/plain';

/* files */
export const getDocuments = (parameters) => (dispatch) => {
  dispatch({
    type: SET_IS_LOADING,
    payload: true,
  });

  return DepsService.restSrv.list(DOCUMENTS_CONTEXT, DepsService.rest.documents.list.url, parameters).then((result) => {
    const documents = dispatch(prepareDocumentsForUI(result.content || []));

    dispatch({
      type: SET_FILES,
      payload: {
        documents,
        meta: evalMeta(result),
      },
    });
  });
};

const prepareDocumentsForUI = (documentList) => (dispatch, getState) => {
  const {
    session: { isAdmin },
    documents: { categories },
  } = getState();

  const mapFileTypeToFileExtension = (fileType) => {
    switch (fileType) {
      case 'vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'xlsx';
      case 'vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'doc';
      case 'plain':
        return 'txt';
      default:
        return fileType;
    }
  };

  return documentList.map((document) => {
    const category = _.find(categories, { documentTypeId: document.type.documentTypeId });
    const mimeType = document.mimeType;
    const publishingStartDate = moment
      .unix(document.publishingStartDate / 1000)
      .utc()
      .format(docDateFormat);
    const documentNameSplitted = document.name.split('.');
    const fileType = mimeType ? mimeType.split('/')[1] : DEFAULT_MIME_TYPE;
    const type = `.${documentNameSplitted[documentNameSplitted.length - 1].toLowerCase()}`;
    const gridDownloadData = {
      id: document.id,
      fileName: document.guiName,
      title: document.guiName,
      customFileName: _.get(document.name.split('.'), '[0]'),
      extension: mapFileTypeToFileExtension(fileType),
    };

    return {
      id: document.id,
      title: document.guiName,
      categoryId: document.type.documentTypeId,
      thumbnail: document.thumbnail,
      tags: document.tags ? document.tags : [],
      dateTimestamp: document.creationDate,
      dateCreated: publishingStartDate || '',
      fileName: document.guiName,
      name: document.name,
      type,
      fileType,
      category,
      locale: document.locale,
      publishingStartDate,
      publishingEndDate: document.publishingEndDate
        ? moment
            .unix(document.publishingEndDate / 1000)
            .utc()
            .format(docDateFormat)
        : '',
      publishingIsActive: isAdmin ? checkPublishingIsActive(document.publishingStartDate, document.publishingEndDate) : undefined,
      gridDownloadData,
    };
  });
};

export const saveDocument = (document) => () => DepsService.restSrv.update(DOCUMENTS_CONTEXT, DepsService.rest.documents.update.url, transformDocumentForUpdate(document), { id: document.id });

const transformDocumentForUpdate = (document) => {
  const thumbnailSource = _.get(document, 'thumbnailData[0].fileSource');
  let thumbnailToSend = thumbnailSource ? thumbnailSource.split(',')[1] : undefined;
  if (document.thumbnailData === null) {
    thumbnailToSend = '';
  }

  return {
    id: document.id,
    name: document.fileName,
    guiName: document.title,
    mimeType: document.fileType,
    tags: document.tags,
    type: {
      documentTypeId: document.categoryId,
    },
    locale: document.locale,
    thumbnail: thumbnailToSend,
    publishingStartDate: moment.utc(document.publishingStartDate).format('x'),
    publishingEndDate: document.publishingEndDate ? moment.utc(document.publishingEndDate).format('x') : undefined,
  };
};

export const uploadDocument = (document) => () => DepsService.restSrv.create(DOCUMENTS_CONTEXT, DepsService.rest.documents.create.url, transformDocumentForCreation(document));

const transformDocumentForCreation = (documentToTransform) => {
  const document = Object.assign({}, documentToTransform.formData);
  const thumbnailSource = _.get(documentToTransform, 'thumbnailData[0].fileSource');
  const fileData = _.get(documentToTransform, 'fileData[0]');

  return {
    name: fileData.name || '',
    guiName: document.name,
    source: fileData.fileSource ? fileData.fileSource.split(',')[1] : '',
    tags: document.tags ? document.tags : [],
    mimeType: fileData.fileType || DEFAULT_MIME_TYPE,
    thumbnail: thumbnailSource ? thumbnailSource.split(',')[1] : '',
    type: {
      documentTypeId: document.categoryId,
    },
    locale: document.locale,
    publishingStartDate: document.publishingStartDate,
    publishingEndDate: document.publishingEndDate ? document.publishingEndDate : undefined,
  };
};

const checkPublishingIsActive = (publishingStartDate, publishingEndDate) => {
  if (!publishingStartDate) return false;

  const startDate = moment.unix(publishingStartDate / 1000);
  if (!publishingEndDate) return moment.unix(DepsService.config.serverTime).isAfter(startDate);

  const endDate = moment.unix(publishingEndDate / 1000);
  return moment.unix(DepsService.config.serverTime).isBetween(startDate, endDate, 'days', '[]');
};

export const removeDocument = (document) => () => DepsService.restSrv.remove(DOCUMENTS_CONTEXT, DepsService.rest.documents.remove.url, { id: document.id }, { data: { name: document.title } });

export const downloadDocument = (document) => () => DepsService.restSrv.downloadBlob(DOCUMENTS_CONTEXT, `${DepsService.rest.documents.download.url}/${document.id}`);

/* categories */
export const getCategories = () => (dispatch) =>
  DepsService.restSrv.list(DOCUMENTS_CONTEXT, DepsService.rest.documents.categories.url, { locale: '*' }).then((categories) => {
    dispatch({
      type: SET_CATEGORIES,
      payload: categories,
    });
  });

export const createCategory = (category) => (dispatch) => DepsService.restSrv.create(DOCUMENTS_CONTEXT, DepsService.rest.documents.category.create.url, category).then(() => dispatch(getCategories()));

export const updateCategory = (category) => (dispatch) =>
  DepsService.restSrv
    .update(DOCUMENTS_CONTEXT, DepsService.rest.documents.category.update.url, category, {
      id: category.documentTypeId,
    })
    .then(() => dispatch(getCategories()));

export const removeCategory = (category) => (dispatch) => DepsService.restSrv.remove(DOCUMENTS_CONTEXT, DepsService.rest.documents.category.remove.url, { id: category.documentTypeId }).then(() => dispatch(getCategories()));

/* tags */
export const getTags = () => (dispatch) =>
  DepsService.restSrv.list(DOCUMENTS_CONTEXT, DepsService.rest.documents.tagsList.url).then((tags) => {
    dispatch({
      type: SET_TAGS,
      payload: tags.tags,
    });
  });



// WEBPACK FOOTER //
// ./src/modules/documents/actions/documents.actions.js